/**
 * Public Layout
 * Layout for public-facing pages (landing page, about, contact, etc.)
 */
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Clock, ChevronDown, Menu, X, MapPin, Globe } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';
import { ROUTES } from '../../constants/routes';
import saccosLogo from '../../assets/logos/saccos_logo.png';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { isSafeUrl } from '../../utils/isSafeUrl';

/**
 * Public Header Component
 */
const PublicHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [topHeader, setTopHeader] = useState(null);
  const [navData, setNavData] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  const isActive = (path) => location.pathname === path;
  const isParentActive = (dropdown) => dropdown.some((item) => location.pathname === item.path);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
    setOpenMobileDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch top header info from CMS
  useEffect(() => {
    const fetchTopHeader = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}info/get`,
          { InfoId: 100 },
          { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
        );
        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const item = response.data.data[0];
          if (item && !item.IsDeleted && item.IsPublished) {
            let parsed = {};
            if (item.Description && typeof item.Description === 'string') {
              try {
                parsed = JSON.parse(item.Description);
              } catch {
                parsed = {};
              }
            } else if (item.Description && typeof item.Description === 'object') {
              parsed = item.Description;
            }
            setTopHeader({ ...item, parsed });
          }
        }
      } catch (error) {
        console.error('Failed to fetch top header data:', error);
      }
    };
    fetchTopHeader();
  }, []);

  // Fetch nav bar info from CMS
  useEffect(() => {
    const fetchNavData = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}info/get`,
          { InfoId: 102 },
          { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
        );
        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const item = response.data.data[0];
          if (item && !item.IsDeleted) {
            setNavData(item);
          }
        }
      } catch (error) {
        console.error('Failed to fetch nav data:', error);
      }
    };
    fetchNavData();
  }, []);


  const navLinks = [
    { label: 'Home', path: ROUTES.HOME },
    {
      label: 'About Us',
      path: '/about',
      dropdown: [
        { label: 'About Organization', path: '/about' },
        { label: 'Organization Structure', path: '/about/organization-structure' },
      ],
    },
    {
      label: 'Products',
      path: '/products',
      dropdown: [
        { label: 'Savings & Shares', path: '/savings-shares' },
        { label: 'Loan Products', path: '/loan-products' },
      ],
    },
    {
      label: 'Media Centre',
      path: '/media',
      dropdown: [
        { label: 'News and Events', path: '/news-events' },
        { label: 'Downloads', path: '/downloads' },
      ],
    },
    ...(navData && navData.Description && navData.Link
      ? [
          {
            label: navData.Description,
            path: navData.Link,
            isButton: true,
            isExternal: navData.Link.startsWith('http'),
          },
        ]
      : []),
    { label: 'Contact Us', path: '/contact' },
  ];

  const topHeaderData = topHeader?.parsed || {};
  const email = topHeaderData.email;
  const phone = topHeaderData.phone;
  const emailHref = email ? `mailto:${email.trim()}` : null;
  const phoneHref = phone && phone.split('|')[0]
    ? `tel:${phone.split('|')[0].trim().replace(/\s/g, '')}`
    : null;
  const EmailIcon = topHeaderData.emailIcon ? LucideIcons[topHeaderData.emailIcon] : null;
  const PhoneIcon = topHeaderData.phoneIcon ? LucideIcons[topHeaderData.phoneIcon] : null;
  const socials = topHeaderData.socials || [];
  return (
    <header className="bg-[#f8f1e5] shadow-sm sticky top-0 z-50">
      {/* Top Bar */}
      {topHeader && (
        <div className="bg-[#2f3951] text-white py-2 border-b-4 border-[#ca9a37]">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 flex justify-between items-center text-sm">
          <div className="flex items-center space-x-6">
            {email && (
              <a
                href={emailHref}
                className="flex items-center hover:text-[#ca9a37] transition-colors"
              >
                {EmailIcon && <EmailIcon className="w-4 h-4 mr-2" />}
                <span className="hidden sm:inline">{email}</span>
              </a>
            )}
            {phone && (
              <a
                href={phoneHref}
                className="flex items-center hover:text-[#ca9a37] transition-colors"
              >
                {PhoneIcon && <PhoneIcon className="w-4 h-4 mr-2" />}
                <span className="hidden sm:inline">{phone}</span>
              </a>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {socials.length > 0 &&
              socials.map((social, idx) => {
                const SocialIcon = social.icon ? LucideIcons[social.icon] : null;
                const isLink =
                  social.link &&
                  (social.link.startsWith('http://') ||
                    social.link.startsWith('https://') ||
                    social.link.startsWith('/'));
                const content = (
                  <>
                    {SocialIcon && <SocialIcon className="w-4 h-4 mr-2" />}
                    <span className="hidden md:inline">{social.link}</span>
                  </>
                );
                return isLink ? (
                  <a
                    key={idx}
                    href={isSafeUrl(social.link) ? social.link : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:flex items-center hover:text-[#ca9a37] transition-colors"
                  >
                    {content}
                  </a>
                ) : (
                  <span key={idx} className="hidden md:flex items-center">
                    {content}
                  </span>
                );
              })}
          </div>
        </div>
      </div>
      )}

      {/* Main Navigation */}
      <div className="container mx-auto px-6 md:px-8 lg:px-12 py-3 md:py-0">
        <div className="flex items-center justify-between">
          {/* nav bar Logo */}
          <Link to={ROUTES.HOME} className="flex items-center space-x-3 md:space-x-4">
            <img
              src={saccosLogo}
              alt="Uhamiaji SACCOS Logo"
              className="h-16 md:h-20 w-auto object-contain"
            />
            <div>
              <h1 className="text-[#2f3951] font-extrabold text-xl md:text-2xl lg:text-[1.75rem] leading-tight tracking-wide">
                {navData?.Title}
              </h1>
              <p className="text-[#ca9a37] text-sm md:text-base font-medium tracking-wide mt-0.5">
                {navData?.SubTitle}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav ref={navRef} className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div key={link.label} className="relative group">
                {link.dropdown ? (
                  <>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                      className={`flex items-center font-medium transition-colors ${isParentActive(link.dropdown)
                          ? 'text-[#ca9a37]'
                          : 'text-[#2f3951] hover:text-[#ca9a37]'
                        }`}
                    >
                      {link.label}
                      <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${openDropdown === link.label ? 'rotate-180' : ''}`} />
                    </button>
                    <div
                      className={`absolute top-full left-0 mt-2 w-48 bg-white shadow-lg rounded-lg py-2 border border-gray-100 transition-all duration-200 origin-top-left z-50 ${openDropdown === link.label
                          ? 'opacity-100 scale-100 visible'
                          : 'opacity-0 scale-95 invisible pointer-events-none'
                        }`}
                    >
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.path}
                          onClick={() => setOpenDropdown(null)}
                          className={`block px-4 py-2 ${isActive(item.path)
                              ? 'text-[#ca9a37] bg-[#f8f1e5] font-medium'
                              : 'text-[#2f3951] hover:bg-[#f8f1e5] hover:text-[#ca9a37]'
                            }`}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : link.isButton ? (
                  link.isExternal ? (
                    <a
                      href={isSafeUrl(link.path) ? link.path : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#2f3951] hover:bg-[#ca9a37] text-white px-4 py-2 rounded font-medium transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className="bg-[#2f3951] hover:bg-[#ca9a37] text-white px-4 py-2 rounded font-medium transition-colors"
                    >
                      {link.label}
                    </Link>
                  )
                ) : (
                  <Link
                    to={link.path}
                    className={`font-medium transition-colors ${isActive(link.path)
                        ? 'text-[#ca9a37]'
                        : 'text-[#2f3951] hover:text-[#ca9a37]'
                      }`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-700"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-4 py-4 border-t border-gray-100">
            {navLinks.map((link) => (
              <div key={link.label}>
                {link.dropdown ? (
                  <div className="py-2">
                    <button
                      onClick={() => setOpenMobileDropdown(openMobileDropdown === link.label ? null : link.label)}
                      className={`flex items-center justify-between w-full px-4 py-2 font-medium text-left ${isParentActive(link.dropdown) ? 'text-[#ca9a37]' : 'text-[#2f3951]'}`}
                    >
                      {link.label}
                      <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${openMobileDropdown === link.label ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-200 ${openMobileDropdown === link.label ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.path}
                          className={`block px-8 py-2 ${isActive(item.path)
                              ? 'text-[#ca9a37] font-medium'
                              : 'text-[#2f3951] hover:text-[#ca9a37]'
                            }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  link.isExternal ? (
                    <a
                      href={isSafeUrl(link.path) ? link.path : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block px-4 py-2 ${link.isButton
                          ? 'text-[#ca9a37] font-semibold'
                          : 'text-[#2f3951]'
                        }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className={`block px-4 py-2 ${link.isButton
                          ? 'text-[#ca9a37] font-semibold'
                          : isActive(link.path)
                            ? 'text-[#ca9a37] font-medium'
                            : 'text-[#2f3951]'
                        }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </div>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

/**
 * Public Footer Component
 */
const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const OPEN_HOURS_SECTION_UID = 'OPEN-HOURS-SECTION';

const getOfficeContactIcon = (item) => {
  const text = `${item.Title || ''} ${item.SubTitle || ''} ${(item.Description || '').replace(/<[^>]*>/g, '')}`.toLowerCase();
  // location / address (English + Swahili: anwani, makao, ofisi, mkoa, jiji, barabara, mtaa)
  if (text.includes('address') || text.includes('location') || text.includes('street') || text.includes('road') ||
    text.includes('plot') || text.includes('dar') || text.includes('anwani') || text.includes('makao') ||
    text.includes('ofisi') || text.includes('barabara') || text.includes('mtaa') || text.includes('jiji')) return MapPin;
  // phone / mobile (English + Swahili: simu, nambari)
  if (text.includes('phone') || text.includes('mobile') || text.includes('call') || text.includes('tel') ||
    text.includes('simu') || text.includes('nambari') || /\+?\d[\d\s]{6,}/.test(text)) return Phone;
  // email (English + Swahili: barua pepe)
  if (text.includes('email') || text.includes('mail') || text.includes('@') || text.includes('barua pepe') || text.includes('barua-pepe')) return Mail;
  // hours / time (English + Swahili: masaa, saa, wakati)
  if (text.includes('hour') || text.includes('time') || text.includes('open') || text.includes('masaa') || text.includes('wakati')) return Clock;
  return Globe;
};

const PublicFooter = () => {
  const [helpSupport, setHelpSupport] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [openHours, setOpenHours] = useState([]);
  const [officeContacts, setOfficeContacts] = useState([]);
  const [copyrightLinks, setCopyrightLinks] = useState([]);

  useEffect(() => {
    const fetchFooterData = async () => {
      const [helpRes, quickRes, openHoursRes, officeRes, copyrightRes] = await Promise.allSettled([
        axios.post(`${CMS_API_URL}info/by-category`, { CategoryId: 48 }, { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }),
        axios.post(`${CMS_API_URL}info/by-category`, { CategoryId: 49 }, { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }),
        axios.post(`${CMS_API_URL}sections/get-by-uid`, { UID: OPEN_HOURS_SECTION_UID }, { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }),
        axios.post(`${CMS_API_URL}info/by-category`, { CategoryId: 52 }, { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }),
        axios.post(`${CMS_API_URL}info/by-category`, { CategoryId: 51 }, { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }),
      ]);

      const parseItems = (res) => {
        if (res.status === 'fulfilled' && res.value.data?.code === 1 && Array.isArray(res.value.data?.data)) {
          return res.value.data.data
            .filter((item) => item.IsDeleted === false && item.IsPublished === true)
            .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));
        }
        return [];
      };

      const parseOpenHours = (res) => {
        if (res.status === 'fulfilled' && res.value.data?.code === 1 && Array.isArray(res.value.data?.data) && res.value.data.data.length > 0) {
          const item = res.value.data.data[0];
          if (item.IsDeleted === false && item.IsPublished === true) {
            return [item];
          }
        }
        return [];
      };

      setHelpSupport(parseItems(helpRes));
      setQuickLinks(parseItems(quickRes));
      setOpenHours(parseOpenHours(openHoursRes));
      setOfficeContacts(parseItems(officeRes));
      setCopyrightLinks(parseItems(copyrightRes));
    };

    fetchFooterData();
  }, []);

  const staffMailLink = copyrightLinks.find((item) =>
    (item.SubTitle || '').toLowerCase().includes('staff mail')
  );

  return (
    <footer className="bg-[#2f3951] text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-6 md:px-8 lg:px-12 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Help & Support */}
          <div>
            <h3 className="font-bold text-lg mb-4 border-b-2 border-yellow-500 pb-2 inline-block">
              {helpSupport[0]?.Title}
            </h3>
            <ul className="space-y-2 text-gray-300">
              {helpSupport.map((item) => {
                const sub = (item.SubTitle || '').trim().toLowerCase();
                const overrides = {
                  faqs: '/contact#support',
                  feedback: '/contact#send-message',
                  complaints: '/contact#send-message',
                  'customer guide': '/services/become-a-member',
                };
                const to = overrides[sub] || item.Link || '/';
                return (
                  <li key={item.InfoId}>
                    <Link to={to} className="hover:text-yellow-400 transition-colors">
                      {item.SubTitle}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Office & Contacts */}
          <div>
            <h3 className="font-bold text-lg mb-4 border-b-2 border-yellow-500 pb-2 inline-block">
              {officeContacts[0]?.Title}
            </h3>

            <ul className="space-y-3 text-gray-300 text-sm">
              {officeContacts.map((item) => (
                <li key={item.InfoId}>
                  <div className="space-y-2">
                    {/* Address */}
                    {item.Description && (
                      <div className="flex items-start">
                        {/* <MapPin className="w-4 h-4 mr-2 mt-0.5 text-yellow-500 flex-shrink-0" /> */}
                        <span className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0">📍</span>
                        <div
                          className="text-gray-400 w-full max-w-full break-words leading-relaxed"
                          style={{ wordBreak: 'normal' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.Description) }}
                        />
                      </div>
                    )}

                    {/* Phone */}
                    {item.SubTitle && (
                      <div className="flex items-center">
                        {/* <Phone className="w-4 h-4 mr-2 text-yellow-500 flex-shrink-0" /> */}
                        <span className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0">📞</span>
                        <span>{item.SubTitle}</span>
                      </div>
                    )}

                    {/* Email */}
                    {item.Link && (
                      <div className="flex items-center">
                        {/* <Mail className="w-4 h-4 mr-2 text-yellow-500 flex-shrink-0" /> */}
                        <span className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0">✉️</span>
                        <a
                          href="/contact#send-message"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-yellow-400 transition-colors"
                        >
                          {item.Link}
                        </a>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4 border-b-2 border-yellow-500 pb-2 inline-block">
              {quickLinks[0]?.Title}
            </h3>
            <ul className="space-y-2 text-gray-300">
              {quickLinks.map((item) => {
                const sub = (item.SubTitle || '').trim().toLowerCase();
                const isNewTab = ['apply for loan', 'ess watumishi portal', 'saccos customer portal'].includes(sub);
                const isBecomeMember = sub === 'become a member';
                return (
                  <li key={item.InfoId}>
                    {isNewTab ? (
                      <a
                        href={isSafeUrl(item.Link) ? item.Link : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-yellow-400 transition-colors"
                      >
                        {item.SubTitle}
                      </a>
                    ) : isBecomeMember ? (
                      <Link to="/services/become-a-member" className="hover:text-yellow-400 transition-colors">
                        {item.SubTitle}
                      </Link>
                    ) : (
                      <Link to={item.Link || '/'} className="hover:text-yellow-400 transition-colors">
                        {item.SubTitle}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Open Hours */}
          {openHours[0] && (
            <div>
              <h3 className="font-bold text-lg mb-4 border-b-2 border-yellow-500 pb-2 inline-block">
                {openHours[0]?.Title}
              </h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                {openHours.map((item) => (
                  <li key={item.SectionId || item.InfoId}>
                    <div
                      className="text-gray-300"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.Description) }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-gray-400 mb-4 md:mb-0">
              <Link
                to="/privacy-policy"
                className="hover:text-[#ca9a37] transition-colors"
              >
                Privacy Policy
              </Link>

              <span className="text-gray-400 select-none">|</span>

              <a
                href={(staffMailLink && isSafeUrl(staffMailLink.Link) && staffMailLink.Link) || 'https://webmail.uhamiajisaccos.or.tz'}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#ca9a37] transition-colors"
              >
                Login: Staff Mail
              </a>

              <span className="text-gray-400 select-none">|</span>

              <Link
                to="/downloads"
                className="hover:text-[#ca9a37] transition-colors"
              >
                Downloads
              </Link>
            </div>
            <p>{copyrightLinks.find((item) => !item.Link || item.Link.trim() === '')?.Title}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

/**
 * Public Layout Component
 * @returns {JSX.Element} Public layout with header and footer
 */
const PublicLayout = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />
      <main>
        <Outlet />
      </main>
      <PublicFooter className="mt-auto" />
    </div>
  );
};

export default PublicLayout;
